import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Work.css';

gsap.registerPlugin(ScrollTrigger);

const Work = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const workRef = useRef(null);
  const cardsRef = useRef([]);

  const workData = [
    {
      id: 1,
      title: "UX Product Design",
      category: "ux",
      description: "EcoSync was designed for a UX brief focused on mapping user journeys, task models, and wireflows. The app encourages everyday environmental action through solar tracking, rewards, and community-driven features.",
      image: "/images/EcosyncUX_IMG.png",
      url: "/EcosyncUX.pdf"
    },
    {
      id: 2,
      title: "Cross-Channel Content Strategy",
      category: "other",
      description: "This project focused on creating a digital content strategy for a social campaign linked to our Term 1 service design work. It explored how cross-platform storytelling, visual design, and user engagement can support a cohesive, accessible, and scalable service experience.",
      image: "/images/ContentStrat_IMG.png",
      url: "/CrossChannel.pdf"
    },
    {
      id: 3,
      title: "Photography Portfolio",
      category: "web",
      description: "I was tasked to create a simple portfolio for my photography work as a student. Note: I'm not a photographer.",
      image: "/images/PhotoWebsite.png",
      url: "/photography.html"
    },
    {
      id: 4,
      title: "Basic Landing Page",
      category: "web",
      description: "For this project, I had to use basic HTML, CSS and JavaScript to create a basic landing page, I chose to create one for a fictional company (with a hidden surprise!).",
      image: "/images/LandingPage_IMG.png",
      url: "/LandingPage/index.html"
    },
    {
      id: 5,
      title: "Farm-to-Table Grocery App",
      category: "ux",
      description: "Farmstead is a farm-to-table grocery app designed to connect consumers directly with local farmers. It focuses on ethical pricing, seasonal produce, and transparency—making fresh food more accessible while supporting a fairer, more sustainable food system.",
      image: "/images/Farmstead_IMG.png",
      url: "/Farmstead.pdf"
    }
  ];

  const filters = [
    { id: 'all', label: 'All Work' },
    { id: 'web', label: 'Web Design' },
    { id: 'ux', label: 'UX/UI' },
    { id: 'other', label: 'Miscellaneous' }
  ];

  const filteredWork = activeFilter === 'all' 
    ? workData 
    : workData.filter(item => item.category === activeFilter);

  useEffect(() => {
    if (!workRef.current) return;

    // Animate work cards on scroll
    cardsRef.current.forEach((card, index) => {
      gsap.fromTo(
        card,
        {
          opacity: 0,
          y: 50,
          scale: 0.9,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          delay: index * 0.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            end: "bottom 15%",
            toggleActions: "play none none reverse"
          }
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [filteredWork]);

  return (
    <section id="work" className="work-section" ref={workRef}>
      <div className="work-container">
        {/* Left Side - Sticky Title and Filters */}
        <div className="work-left">
          <div className="work-sticky">
            <h2 className="work-title">Work</h2>
            <div className="work-filters">
              {filters.map(filter => (
                <button
                  key={filter.id}
                  className={`filter-btn ${activeFilter === filter.id ? 'active' : ''}`}
                  onClick={() => setActiveFilter(filter.id)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Work Cards */}
        <div className="work-right">
          <div className="work-grid">
            {filteredWork.map((item, index) => (
              <a
                key={item.id}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="work-card"
                ref={el => cardsRef.current[index] = el}
              >
                <div className="card-image">
                  <img src={item.image} alt={item.title} />
                </div>
                <div className="card-content">
                  <h3 className="card-title">{item.title}</h3>
                  <p className="card-description">{item.description}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Work;